import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { FullScreenError } from '@/components/custom-ui/FullScreenError'
import { FullScreenLoader } from '@/components/custom-ui/FullScreenLoader'
import { InvestigationDirtyNotifier } from '@/components/investigations/InvestigationDirtyNotifier'
import { SelectionBridge } from '@/components/investigations/SelectionBridge'
import type { GraphAction } from '@/components/langgraph-chat/types'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'
import { CaseAsOfProvider } from '@/features/case-as-of/CaseAsOf'
import { useCaseDetail } from '@/features/cases/hooks/useCaseDetail'
import { useCaseNavigation } from '@/features/cases/hooks/useCaseNavigation'
import { MonitorLiveUpdates } from '@/features/monitor/MonitorLiveUpdates'
import { ApiError } from '@/services/api'
import { FilterStoreProvider } from '@/stores/filter-store-provider'
import { InvestigationsStoreProvider } from '@/stores/investigations-store-provider'
import { CaseContextProvider } from './CaseContext'
import { useInvestigateContextOptional } from './InvestigateContext'

export type { CaseOutletContext } from '@/features/cases/navigation'

function CaseLayoutInner({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const investigate = useInvestigateContextOptional()
  const agent = useAgentSidebar()
  const caseQuery = useCaseDetail(caseId)
  const { viewType, navigateToView, handleWorkspaceChange, handleViewGeoData } =
    useCaseNavigation(caseId)

  const outletCtx = useMemo(
    () => ({ navigateToView, handleViewGeoData, handleWorkspaceChange }),
    [navigateToView, handleViewGeoData, handleWorkspaceChange]
  )

  // Route case-scoped graph actions from the shell-level agent sidebar.
  const handleAIGraphAction = useCallback(
    (action: GraphAction) => {
      if (action.action_type === 'show_path' && viewType !== 'knowledge-graph') {
        toast.info(t('common.switchingToGraph'))
        investigate?.setPendingGraphAction(action)
        navigateToView('knowledge-graph')
      } else if (investigate) {
        investigate.dispatchGraphAction(action)
      } else {
        navigateToView('knowledge-graph')
      }
    },
    [viewType, investigate, navigateToView, t]
  )

  // Skip registration on the knowledge-graph view — that page registers its own
  // handler against the cytoscape instance and parent effects would clobber it.
  useEffect(() => {
    if (viewType === 'knowledge-graph') return
    agent.registerGraphActionHandler(handleAIGraphAction)
    return () => agent.registerGraphActionHandler(null)
  }, [agent, handleAIGraphAction, viewType])

  if (caseQuery.isLoading) return <FullScreenLoader />

  if (caseQuery.isError || !caseQuery.data) {
    const is404 = caseQuery.error instanceof ApiError && caseQuery.error.status === 404
    return (
      <FullScreenError
        title={is404 ? t('cases.layout.notFound') : t('cases.layout.failedToLoad')}
      />
    )
  }

  return (
    <CaseContextProvider
      value={{ caseId, caseData: caseQuery.data, refetchCase: () => caseQuery.refetch() }}
    >
      <FilterStoreProvider caseId={caseId}>
        <InvestigationsStoreProvider caseId={caseId}>
          <SelectionBridge />
          <InvestigationDirtyNotifier />
          {/* The bell and the Monitor inbox update without a reload while the case is open. */}
          <MonitorLiveUpdates caseId={caseId} />
          {/* *Spis k dátumu* stays with the case while the reader moves through its pages. */}
          <CaseAsOfProvider caseId={caseId}>
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              <Outlet context={outletCtx} />
            </div>
          </CaseAsOfProvider>
        </InvestigationsStoreProvider>
      </FilterStoreProvider>
    </CaseContextProvider>
  )
}

export default function CaseLayout() {
  const { t } = useTranslation()
  const { caseId } = useParams<{ caseId: string }>()
  if (!caseId) return <FullScreenError title={t('cases.layout.idMissing')} />
  return <CaseLayoutInner caseId={caseId} />
}
